"use client"

import { useGSAP } from "@gsap/react"
import gsap from "gsap"

gsap.registerPlugin(useGSAP)

/**
 * 404 entrance + idle animation (GSAP per CLAUDE.md). Renders nothing — mounted
 * by `(store)/not-found.tsx`, it animates that SERVER-rendered markup through
 * `data-nf-*` hooks so the 404 stays an RSC and reads fine without JS (fromTo,
 * never pre-hidden). Reduced-motion users get the static page (matchMedia guard).
 *
 * Sequence: icon pops in (back.out) then floats + tilts forever; the ring behind
 * it breathes; title/description/action stagger up under it. RTL-safe — tilt
 * mirrors on `dir="rtl"` and text is animated per element (never char-split, so
 * Persian shaping is intact).
 */
export function NotFoundAnimation() {
  useGSAP(() => {
    const mm = gsap.matchMedia()

    mm.add("(prefers-reduced-motion: no-preference)", () => {
      const rtl = document.documentElement.dir === "rtl"

      const tl = gsap.timeline({ defaults: { ease: "power3.out" } })

      if (document.querySelector("[data-nf-icon]")) {
        tl.fromTo(
          "[data-nf-icon]",
          { autoAlpha: 0, scale: 0.4, rotation: rtl ? 18 : -18 },
          {
            autoAlpha: 1,
            scale: 1,
            rotation: 0,
            duration: 0.6,
            ease: "back.out(1.7)",
          }
        )
        // Idle float + gentle tilt loop after the entrance settles.
        gsap.to("[data-nf-icon]", {
          y: -8,
          rotation: rtl ? -4 : 4,
          duration: 1.8,
          ease: "sine.inOut",
          yoyo: true,
          repeat: -1,
          delay: 0.6,
        })
      }

      if (document.querySelector("[data-nf-ring]")) {
        // Soft breathing halo behind the icon.
        gsap.fromTo(
          "[data-nf-ring]",
          { scale: 0.85, autoAlpha: 0.35 },
          {
            scale: 1.1,
            autoAlpha: 0.7,
            duration: 2.2,
            ease: "sine.inOut",
            yoyo: true,
            repeat: -1,
          }
        )
      }

      const copy = [
        "[data-nf-title]",
        "[data-nf-desc]",
        "[data-nf-action]",
      ].filter((s) => document.querySelector(s))
      if (copy.length > 0) {
        tl.fromTo(
          copy,
          { autoAlpha: 0, y: 16 },
          { autoAlpha: 1, y: 0, duration: 0.5, stagger: 0.1 },
          "-=0.25"
        )
      }
    })
  })

  return null
}
